import React, { useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest, { shippingAccessScope } from '../../hooks/useShippingRequest';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useT } from '../../i18n/LanguageContext';
import {
  readPurchaseReceipt,
  samePurchaseQuote,
  purchaseState,
} from '../distributor/distributorPurchaseReceipt';

const referencePattern = /^[A-Za-z0-9][A-Za-z0-9._:-]{2,79}$/;
const requestPattern = /^[A-Za-z0-9_-]{16,100}$/;
function readRecovery(response, original) {
  const value = response?.data?.data;
  const purchase = readPurchaseReceipt({ data: { data: value?.purchase } });
  const attempt = value.attempt;
  const observed = purchase.achAttempt || purchase.cardAttempt;
  const support = value.supportCase;
  if (
    purchase._id !== original._id ||
    !samePurchaseQuote(purchase, original) ||
    purchase.createdAt !== original.createdAt ||
    !attempt ||
    !observed ||
    attempt.method !== purchase.method ||
    attempt.state !== observed.state ||
    !Number.isSafeInteger(attempt.revision) ||
    attempt.revision < 0 ||
    attempt.requestId !== purchase._id ||
    !/^[a-f\d]{64}$/.test(attempt.intentHash) ||
    !['sandbox', 'production'].includes(attempt.environment) ||
    typeof value.canRecordSupportCase !== 'boolean' ||
    (support !== null &&
      (!support ||
        !requestPattern.test(support.requestKey) ||
        !referencePattern.test(support.providerCaseReference) ||
        !Number.isFinite(Date.parse(support.recordedAt)) ||
        Date.parse(support.recordedAt) < Date.parse(purchase.createdAt))) ||
    (value.canRecordSupportCase &&
      (support !== null ||
        purchase.status !== 'prepared' ||
        !purchase.open ||
        !['processing', 'unknown', 'failed', 'declined'].includes(attempt.state)))
  )
    throw new Error('Invalid payment recovery');
  return value;
}

/** An internal case note preserves uncertainty. It never initiates or resolves a payment. */
export default function DistributorPaymentSupportCase({
  distributorId,
  purchase,
  disabled,
  onPendingChange,
}) {
  const t = useT();
  const [value, setValue] = useState(null);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [intent, setIntent] = useState(null);
  const [error, setError] = useState('');
  const pending = useRef(false);
  const intentRef = useRef(null);
  const session = useRef(captureSession());
  const scope = useRef(shippingAccessScope());
  const { begin } = useShippingRequest(`${distributorId}:${purchase._id}`);
  const current = () =>
    isSessionCurrent(session.current) && scope.current === shippingAccessScope();
  const load = async () => {
    if (!current() || pending.current || disabled || intentRef.current) return;
    pending.current = true;
    setBusy(true);
    setError('');
    onPendingChange?.(true);
    const request = begin();
    try {
      const result = await distributorService.paymentRecovery(distributorId, purchase._id, {
        signal: request.signal,
      });
      if (request.isCurrent() && current()) setValue(readRecovery(result, purchase));
    } catch {
      if (request.isCurrent() && current())
        setError(
          'Support case details are unavailable. Reload review records before continuing. The saved payment remains unchanged.'
        );
    } finally {
      if (request.isCurrent() && current()) {
        pending.current = false;
        setBusy(false);
        onPendingChange?.(false);
      }
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!current() || pending.current || disabled) return;
    let captured = intentRef.current;
    if (!captured) {
      if (!value?.canRecordSupportCase || !referencePattern.test(reference)) return;
      const requestKey = window.crypto?.randomUUID?.();
      if (!requestKey || !requestPattern.test(requestKey)) {
        setError('A secure request key is unavailable. Reload before reviewing.');
        return;
      }
      captured = {
        requestKey,
        expectedRevision: value.attempt.revision,
        expectedIntentHash: value.attempt.intentHash,
        providerCaseReference: reference,
      };
      intentRef.current = captured;
      setIntent(captured);
    }
    pending.current = true;
    setBusy(true);
    setError('');
    onPendingChange?.(true);
    const request = begin();
    try {
      const result = await distributorService.recordPaymentSupportCase(
        distributorId,
        purchase._id,
        captured
      );
      if (!request.isCurrent() || !current()) return;
      const receipt = readRecovery(result, purchase);
      if (
        receipt.supportCase?.requestKey !== captured.requestKey ||
        receipt.supportCase.providerCaseReference !== captured.providerCaseReference ||
        receipt.attempt.intentHash !== captured.expectedIntentHash ||
        receipt.attempt.revision < captured.expectedRevision ||
        receipt.canRecordSupportCase
      )
        throw new Error('Unconfirmed support case');
      setValue(receipt);
      intentRef.current = null;
      setIntent(null);
      onPendingChange?.(false);
    } catch {
      if (request.isCurrent() && current())
        setError(
          'Support case result is unconfirmed. Recover the same case request before recording another.'
        );
    } finally {
      if (request.isCurrent() && current()) {
        pending.current = false;
        setBusy(false);
      }
    }
  };
  return (
    <div className="space-y-2 mt-3">
      <button type="button" disabled={disabled || busy || Boolean(intent)} onClick={load}>
        {t('View payment support case')}
      </button>
      {busy && <p role="status">{t('Checking support case...')}</p>}
      {error && <p role="alert">{t(error)}</p>}
      {value && (
        <>
          <p>
            {t('Original payment request reference')}: {value.attempt.requestId}
          </p>
          <p>
            {t(value.attempt.method === 'ach' ? 'ACH' : 'Credit card')} ·{' '}
            {t(purchaseState(value.purchase))}
          </p>
          <p>
            {t(
              'Recording a support case does not resolve the payment, close the purchase, retry a charge or add units. Review payment evidence separately.'
            )}
          </p>
          {value.supportCase ? (
            <p role="status">
              {t('Support case recorded')}: {value.supportCase.providerCaseReference}
            </p>
          ) : (
            (value.canRecordSupportCase || intent) && (
              <form onSubmit={submit} className="space-y-2">
                <label className="block">
                  {t('Provider support case reference')}
                  <input
                    className="block w-full rounded border p-2"
                    value={reference}
                    maxLength={80}
                    disabled={disabled || busy || Boolean(intent)}
                    onChange={(event) => {
                      if (current()) setReference(event.target.value);
                    }}
                  />
                </label>
                <p>
                  {t(
                    'Enter only the support case reference. Do not enter bank details, credentials or provider responses.'
                  )}
                </p>
                <button
                  type="submit"
                  disabled={disabled || busy || (!intent && !referencePattern.test(reference))}
                >
                  {t(intent ? 'Recover same support case request' : 'Record support case')}
                </button>
              </form>
            )
          )}
          {!value.supportCase && !value.canRecordSupportCase && !intent && (
            <p>{t('Payment status changed. Reload review records.')}</p>
          )}
        </>
      )}
    </div>
  );
}
